import { useEditor, EditorContent, NodeViewWrapper, NodeViewContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { TextAlign } from '@tiptap/extension-text-align';
import { Link } from '@tiptap/extension-link';
import { Image } from '@tiptap/extension-image';
import { Color } from '@tiptap/extension-color';
import { TextStyle } from '@tiptap/extension-text-style';
import { Placeholder } from '@tiptap/extension-placeholder';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { 
  Bars3BottomLeftIcon, 
  Bars3Icon, 
  Bars3BottomRightIcon,
  ChevronDownIcon,
  ListBulletIcon,
  QueueListIcon,
  PhotoIcon,
  CodeBracketIcon,
  PencilIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import {
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  Transition,
} from "@headlessui/react";
import { Fragment } from 'react';
import { Button } from "components/ui";
import './RichTextEditor.css';

// Utility function to clean HTML content for insertion
const cleanHtmlForInsertion = (htmlContent) => {
  if (!htmlContent || typeof htmlContent !== 'string') {
    return '';
  }

  let cleaned = htmlContent;

  // Remove document-level tags and their content
  cleaned = cleaned.replace(/<!DOCTYPE[^>]*>/gi, '');
  cleaned = cleaned.replace(/<\/?html[^>]*>/gi, '');
  cleaned = cleaned.replace(/<head[\s\S]*?<\/head>/gi, '');
  cleaned = cleaned.replace(/<\/?body[^>]*>/gi, '');
  
  // Remove problematic meta tags and scripts
  cleaned = cleaned.replace(/<meta[^>]*>/gi, '');
  cleaned = cleaned.replace(/<script[\s\S]*?<\/script>/gi, '');
  cleaned = cleaned.replace(/<style[\s\S]*?<\/style>/gi, '');
  cleaned = cleaned.replace(/<link[^>]*>/gi, '');
  
  // Remove excessive whitespace and newlines
  cleaned = cleaned.replace(/\s*\n\s*/g, ' ');
  cleaned = cleaned.replace(/\s{2,}/g, ' ');
  
  // Trim leading and trailing whitespace
  cleaned = cleaned.trim();
  
  
  return cleaned;
};

// Custom Image Component with controls
const ImageComponent = ({ node, updateAttributes, deleteNode, editor }) => {
  const [showControls, setShowControls] = useState(false);

  const handleEdit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Get the onImageEdit function from editor options
    const onImageEdit = editor?.options?.onImageEdit;
    if (onImageEdit) {
      onImageEdit(node);
    }
  };

  const handleDelete = (e) => {
    e.preventDefault();
    e.stopPropagation();
    deleteNode();
  };

  return (
    <NodeViewWrapper className="relative inline-block">
      <div
        className="relative inline-block"
        onMouseEnter={() => setShowControls(true)}
        onMouseLeave={() => setShowControls(false)}
      >
        <img
          src={node.attrs.src}
          alt={node.attrs.alt || ''}
          width={node.attrs.width}
          height={node.attrs.height}
          className="block rounded-lg border-0"
          style={{
            width: node.attrs.width ? `${node.attrs.width}px` : 'auto',
            height: node.attrs.height ? `${node.attrs.height}px` : 'auto',
            outline: 'none',
            border: 'none',
          }}
        />
        
        {showControls && (
          <div className="absolute top-2 right-2 flex gap-1 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-600 p-1">
            <button
              onClick={handleEdit}
              className="p-1.5 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-md transition-colors"
              title="Edit image"
            >
              <PencilIcon className="size-3.5" />
            </button>
            <button
              onClick={handleDelete}
              className="p-1.5 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors"
              title="Delete image"
            >
              <TrashIcon className="size-3.5" />
            </button>
          </div>
        )}
      </div>
    </NodeViewWrapper>
  );
};

const RichTextEditor = ({
  value = '',
  onChange,
  placeholder = 'Enter content...',
  label,
  error,
  disabled = false,
  height = '200px',
  className,
  classNames = {},
  onImageInsert,
  imageUrlToInsert,
  imageWidth,
  imageHeight,
  onImageEdit,
  editingImage,
  onHtmlInsert,
  htmlContentToInsert,
  enableVariableAutocomplete = false,
  templateVariables = {},
  ...props
}) => {
  const [, forceUpdate] = useState({});
  const [activeStates, setActiveStates] = useState({});
  const [showVariableAutocomplete, setShowVariableAutocomplete] = useState(false);
  const [filteredVariables, setFilteredVariables] = useState([]);
  const [triggerFrom, setTriggerFrom] = useState(null);
  const [autocompletePosition, setAutocompletePosition] = useState({ top: 0, left: 0 });
  const autocompleteRef = useRef(null);
  const editorWrapperRef = useRef(null);
  const templateVarsRef = useRef(templateVariables);

  const updateActiveStates = (editor) => {
    if (!editor) return;
    
    const newStates = {
      bold: editor.isActive('bold'),
      italic: editor.isActive('italic'),
      strike: editor.isActive('strike'),
      underline: editor.isActive('underline'),
      h1: editor.isActive('heading', { level: 1 }),
      h2: editor.isActive('heading', { level: 2 }),
      h3: editor.isActive('heading', { level: 3 }),
      paragraph: !editor.isActive('heading'),
      bulletList: editor.isActive('bulletList'),
      orderedList: editor.isActive('orderedList'),
      alignLeft: editor.isActive({ textAlign: 'left' }),
      alignCenter: editor.isActive({ textAlign: 'center' }),
      alignRight: editor.isActive({ textAlign: 'right' }),
    };
    
    setActiveStates(newStates);
    forceUpdate({});
  };

  const handleVariableAutocompleteUpdate = (editorInstance) => {
    if (!editorInstance || !enableVariableAutocomplete) {
      setShowVariableAutocomplete(false);
      setTriggerFrom(null);
      return;
    }

    const variableKeys = Object.keys(templateVarsRef.current || {});
    
    if (variableKeys.length === 0) {
      setShowVariableAutocomplete(false);
      setTriggerFrom(null);
      return;
    }

    const { state } = editorInstance;
    const { from } = state.selection;

    // Only support autocomplete when selection is a caret
    if (!state.selection.empty) {
      setShowVariableAutocomplete(false);
      setTriggerFrom(null);
      return;
    }

    // Take all text from start of doc up to cursor
    let textBeforeCursor = '';
    try {
      textBeforeCursor = state.doc.textBetween(0, from, '\n', '\n');
    } catch (e) {
      textBeforeCursor = '';
    }

    const lastOpenIndex = textBeforeCursor.lastIndexOf('{{');

    // If there is no `{{` before the cursor, close autocomplete
    if (lastOpenIndex === -1) {
      setShowVariableAutocomplete(false);
      setTriggerFrom(null);
      return;
    }

    const searchTextRaw = textBeforeCursor.slice(lastOpenIndex + 2);

    // If user just typed `{{`, show all variables
    if (searchTextRaw.length === 0) {
      setFilteredVariables(variableKeys);
      
      // Calculate position based on cursor coordinates
      const coords = editorInstance.view.coordsAtPos(from);
      if (coords && editorWrapperRef.current) {
        const editorRect = editorWrapperRef.current.getBoundingClientRect();
        setAutocompletePosition({
          top: coords.bottom - editorRect.top + 5,
          left: coords.left - editorRect.left
        });
      }
      
      setShowVariableAutocomplete(true);
      // Map character index in textBeforeCursor back to document position
      const triggerPos = from - textBeforeCursor.length + lastOpenIndex;
      setTriggerFrom(triggerPos);
      return;
    }

    // If the segment after `{{` already contains closing braces or whitespace,
    // stop autocomplete and close the dropdown
    if (searchTextRaw.includes('}}') || /\s/.test(searchTextRaw)) {
      setShowVariableAutocomplete(false);
      setTriggerFrom(null);
      return;
    }

    const searchText = searchTextRaw.toLowerCase();
    const filtered = variableKeys.filter((key) =>
      key.toLowerCase().includes(searchText)
    );

    if (filtered.length === 0) {
      setShowVariableAutocomplete(false);
      setTriggerFrom(null);
      return;
    }

    setFilteredVariables(filtered);
    
    // Calculate position based on cursor coordinates
    const coords = editorInstance.view.coordsAtPos(from);
    if (coords && editorWrapperRef.current) {
      const editorRect = editorWrapperRef.current.getBoundingClientRect();
      setAutocompletePosition({
        top: coords.bottom - editorRect.top + 5,
        left: coords.left - editorRect.left
      });
    }
    
    setShowVariableAutocomplete(true);
    const triggerPos = from - textBeforeCursor.length + lastOpenIndex;
    setTriggerFrom(triggerPos);
  };

  const editor = useEditor({
    extensions: [
      StarterKit,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Link.configure({
        openOnClick: false,
      }),
      Image.extend({
        addNodeView() {
          return ReactNodeViewRenderer(ImageComponent);
        },
      }),
      Color,
      TextStyle,
      Placeholder.configure({
        placeholder: placeholder,
      }),
    ],
    content: value,
    editable: !disabled,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      onChange?.(html);
      updateActiveStates(editor);
      handleVariableAutocompleteUpdate(editor);
    },
    onSelectionUpdate: ({ editor }) => {
      updateActiveStates(editor);
      handleVariableAutocompleteUpdate(editor);
    },
    onCreate: ({ editor }) => {
      updateActiveStates(editor);
    },
    editorProps: {
      attributes: {
        class: 'prose prose-sm sm:prose lg:prose-lg xl:prose-2xl mx-auto focus:outline-none min-h-[120px] p-3',
      },
    },
    // Pass the onImageEdit function through editor options
    onImageEdit: onImageEdit,
  });

  useEffect(() => {
    templateVarsRef.current = templateVariables;
  }, [templateVariables]);

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value);
    }
  }, [value, editor]);

  useEffect(() => {
    if (editor && imageUrlToInsert && !editingImage) {
      // Insert new image
      const imageAttrs = { src: imageUrlToInsert };
      if (imageWidth) imageAttrs.width = imageWidth;
      if (imageHeight) imageAttrs.height = imageHeight;
      editor.chain().focus().setImage(imageAttrs).run();
      updateActiveStates(editor);
    }
  }, [imageUrlToInsert, imageWidth, imageHeight, editor, editingImage]);

  useEffect(() => {
    if (editor && htmlContentToInsert) {
      // Clean the HTML content before insertion
      const cleanedHtml = cleanHtmlForInsertion(htmlContentToInsert);
      if (cleanedHtml) {
        // Insert/append content at current cursor position
        editor.chain().focus().insertContent(cleanedHtml).run();
        updateActiveStates(editor);
      }
    }
  }, [htmlContentToInsert]);

  useEffect(() => {
    if (editor && editingImage && imageUrlToInsert) {
      // Update existing image when editing
      const imageAttrs = { src: imageUrlToInsert };
      if (imageWidth) imageAttrs.width = imageWidth;
      if (imageHeight) imageAttrs.height = imageHeight;
      
      // Find the image node and update its attributes
      const { state } = editor;
      const { tr } = state;
      let imagePos = null;
      
      state.doc.descendants((node, pos) => {
        if (node.type.name === 'image' && node.attrs.src === editingImage.attrs.src) {
          imagePos = pos;
          return false; // Stop iteration
        }
      });
      
      if (imagePos !== null) {
        tr.setNodeMarkup(imagePos, null, imageAttrs);
        editor.view.dispatch(tr);
      }
      
      updateActiveStates(editor);
    }
  }, [editingImage, imageUrlToInsert, imageWidth, imageHeight, editor]);

  useEffect(() => {
    if (!enableVariableAutocomplete) return;

    const handleClickOutside = (event) => {
      if (
        autocompleteRef.current &&
        !autocompleteRef.current.contains(event.target)
      ) {
        // Check if click is inside the editor
        const isClickInEditor = editorWrapperRef.current && (
          editorWrapperRef.current.contains(event.target) || 
          editorWrapperRef.current === event.target
        );
        
        // Only close if click is NOT in the editor
        if (!isClickInEditor) {
          setShowVariableAutocomplete(false);
        }
      }
    };

    if (showVariableAutocomplete) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [enableVariableAutocomplete, showVariableAutocomplete]);

  useEffect(() => {
    return () => {
      if (editor) {
        editor.destroy();
      }
    };
  }, [editor]);

  if (!editor) {
    return (
      <div className={`rich-text-editor-wrapper ${className}`}>
        {label && (
          <label className={`input-label ${classNames?.label}`}>
            <span className={`input-label-text ${classNames?.labelText}`}>
              {label}
            </span>
          </label>
        )}
        <div 
          className="animate-pulse bg-gray-200 dark:bg-dark-600 rounded-lg"
          style={{ height }}
        >
          <div className="p-4 text-center text-gray-500 dark:text-dark-400">
            Loading editor...
          </div>
        </div>
      </div>
    );
  }

  const ToolbarButton = ({ onClick, isActive, children, title }) => (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        'h-8 px-2 rounded hover:bg-gray-100 dark:hover:bg-dark-600 transition-colors flex items-center justify-center',
        isActive ? 'bg-primary-100 text-primary-600 dark:bg-primary-900/30 dark:text-primary-400' : 'text-gray-600 dark:text-dark-300'
      )}
      title={title}
    >
      {children}
    </button>
  );

  return (
    <div className={`rich-text-editor-wrapper ${className}`}>
      {label && (
        <label className={`input-label block text-sm font-medium text-gray-700 dark:text-dark-300 mb-1.5 ${classNames?.label}`}>
          <span className={`input-label-text ${classNames?.labelText}`}>
            {label}
          </span>
        </label>
      )}
      
      <div 
        ref={editorWrapperRef}
        className={clsx(
          "rich-text-editor relative border border-gray-300 rounded-lg overflow-visible bg-white dark:bg-dark-900",
          "dark:border-dark-450 hover:border-gray-400 dark:hover:border-dark-400",
          "focus-within:border-primary-600 dark:focus-within:border-primary-500",
          disabled && "opacity-60 cursor-not-allowed bg-gray-150 dark:bg-dark-600",
          error && "border-error dark:border-error-lighter",
          classNames?.wrapper
        )}
        style={{ height }}
      >
        {/* Toolbar */}
        <div className="toolbar flex flex-wrap items-center gap-1 p-2 border-b border-gray-100 dark:border-dark-700 bg-gray-50 dark:bg-dark-800">
          <ToolbarButton
            onClick={() => {
              editor.chain().focus().setParagraph().run();
              updateActiveStates(editor);
            }}
            isActive={activeStates.paragraph}
            title="Paragraph"
          >
            <span className="text-xs font-normal">P</span>
          </ToolbarButton>
          
          <ToolbarButton
            onClick={() => {
              editor.chain().focus().toggleHeading({ level: 1 }).run();
              updateActiveStates(editor);
            }}
            isActive={activeStates.h1}
            title="Heading 1"
          >
            <span className="text-xs font-bold">H1</span>
          </ToolbarButton>
          
          <ToolbarButton
            onClick={() => {
              editor.chain().focus().toggleHeading({ level: 2 }).run();
              updateActiveStates(editor);
            }}
            isActive={activeStates.h2}
            title="Heading 2"
          >
            <span className="text-xs font-bold">H2</span>
          </ToolbarButton>
          
          <ToolbarButton
            onClick={() => {
              editor.chain().focus().toggleHeading({ level: 3 }).run();
              updateActiveStates(editor);
            }}
            isActive={activeStates.h3}
            title="Heading 3"
          >
            <span className="text-xs font-bold">H3</span>
          </ToolbarButton>
          
          <div className="w-px h-6 bg-gray-300 dark:bg-dark-600 mx-1" />
          
          <ToolbarButton
            onClick={() => {
              editor.chain().focus().toggleBold().run();
              updateActiveStates(editor);
            }}
            isActive={activeStates.bold}
            title="Bold"
          >
            <span className="font-bold">B</span>
          </ToolbarButton>
          
          <ToolbarButton
            onClick={() => {
              editor.chain().focus().toggleItalic().run();
              updateActiveStates(editor);
            }}
            isActive={activeStates.italic}
            title="Italic"
          >
            <span className="italic">I</span>
          </ToolbarButton>
          
          <ToolbarButton
            onClick={() => {
              editor.chain().focus().toggleStrike().run();
              updateActiveStates(editor);
            }}
            isActive={activeStates.strike}
            title="Strikethrough"
          >
            <span className="line-through">S</span>
          </ToolbarButton>
          
          <div className="w-px h-6 bg-gray-300 dark:bg-dark-600 mx-1" />
          
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            isActive={editor.isActive('bulletList')}
            title="Bullet List"
          >
            <ListBulletIcon className="size-4" />
          </ToolbarButton>
          
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            isActive={editor.isActive('orderedList')}
            title="Numbered List"
          >
            <QueueListIcon className="size-4" />
          </ToolbarButton>
          
          <div className="w-px h-6 bg-gray-300 dark:bg-dark-600 mx-1" />
          
          <ToolbarButton
            onClick={() => editor.chain().focus().setTextAlign('left').run()}
            isActive={editor.isActive({ textAlign: 'left' })}
            title="Align Left"
          >
            <Bars3BottomLeftIcon className="size-4" />
          </ToolbarButton>
          
          <ToolbarButton
            onClick={() => editor.chain().focus().setTextAlign('center').run()}
            isActive={editor.isActive({ textAlign: 'center' })}
            title="Align Center"
          >
            <Bars3Icon className="size-4" />
          </ToolbarButton>
          
          <ToolbarButton
            onClick={() => editor.chain().focus().setTextAlign('right').run()}
            isActive={editor.isActive({ textAlign: 'right' })}
            title="Align Right"
          >
            <Bars3BottomRightIcon className="size-4" />
          </ToolbarButton>
          
          <div className="w-px h-6 bg-gray-300 dark:bg-dark-600 mx-1" />
          
          {/* Image Upload */}
          <ToolbarButton
            onClick={() => {
              if (onImageInsert) {
                onImageInsert();
              } else {
                const url = prompt('Enter image URL:');
                if (url) {
                  editor.chain().focus().setImage({ src: url }).run();
                  updateActiveStates(editor);
                }
              }
            }}
            title="Insert Image"
          >
            <PhotoIcon className="size-4" />
          </ToolbarButton>
          
          {/* HTML Paste */}
          <ToolbarButton
            onClick={() => {
              if (onHtmlInsert) {
                onHtmlInsert();
              } else {
                const htmlContent = prompt('Paste HTML content:');
                if (htmlContent) {
                  const cleanedHtml = cleanHtmlForInsertion(htmlContent);
                  if (cleanedHtml) {
                    editor.chain().focus().insertContent(cleanedHtml).run();
                    updateActiveStates(editor);
                  }
                }
              }
            }}
            title="Insert HTML"
          >
            <CodeBracketIcon className="size-4" />
          </ToolbarButton>
        </div>

        {/* Editor Content */}
        <div 
          className="editor-content overflow-auto"
          style={{ height: `calc(${height} - 60px)` }}
        >
          <EditorContent editor={editor} />
        </div>

        {enableVariableAutocomplete && showVariableAutocomplete && filteredVariables.length > 0 && (
          <div
            ref={autocompleteRef}
            className="absolute z-[9999] w-64 bg-white dark:bg-dark-800 border border-gray-300 dark:border-dark-600 rounded-lg shadow-xl max-h-60 overflow-y-auto"
            style={{ 
              zIndex: 9999,
              top: `${autocompletePosition.top}px`,
              left: `${autocompletePosition.left}px`
            }}
          >
            <div className="p-2">
              <div className="text-xs text-gray-500 dark:text-dark-400 px-2 py-1 mb-1">
                Select a variable:
              </div>
              {filteredVariables.map((varName) => (
                <div
                  key={varName}
                  onClick={() => {
                    if (!editor) return;
                    const { state } = editor;
                    const { from } = state.selection;
                    const chain = editor.chain().focus();

                    if (triggerFrom != null && triggerFrom <= from) {
                      chain.deleteRange({ from: triggerFrom, to: from });
                    }

                    chain.insertContent(`{{${varName}}} `).run();
                    setShowVariableAutocomplete(false);
                  }}
                  className="px-3 py-2 cursor-pointer rounded-md transition-colors hover:bg-gray-100 dark:hover:bg-dark-700 text-gray-700 dark:text-dark-300"
                >
                  <code className="text-sm font-mono">{varName}</code>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {error && typeof error === 'string' && (
        <p className={`mt-1 text-xs text-error dark:text-error-light ${classNames?.error}`}>
          {error}
        </p>
      )}
    </div>
  );
};

RichTextEditor.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  label: PropTypes.string,
  error: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  disabled: PropTypes.bool,
  height: PropTypes.string,
  className: PropTypes.string,
  classNames: PropTypes.object,
  onImageInsert: PropTypes.func,
  imageUrlToInsert: PropTypes.string,
  imageWidth: PropTypes.string,
  imageHeight: PropTypes.string,
  onImageEdit: PropTypes.func,
  editingImage: PropTypes.object,
  onHtmlInsert: PropTypes.func,
  htmlContentToInsert: PropTypes.string,
  enableVariableAutocomplete: PropTypes.bool,
  templateVariables: PropTypes.object,
};

export default RichTextEditor;
